import {
  Box,
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  Flex,
  Grid,
  GridItem,
  HStack,
  Text,
  Heading,
  IconButton,
  Skeleton,
  StackProps,
  TextProps,
  Tabs,
  TabList,
  Tab,
  Table,
  Tbody,
  Thead,
  Td,
  Tr,
  Th,
  TabPanels,
  TabPanel,
  TableContainer,
  VStack,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import * as _ from 'lodash';
import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';

import { date as commonDate, finleyAssert } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import {
  ReportTypeGQLType,
  BankAccountWithInstitutionAndDesignationGQLType,
  useGetFundingRequestWithBaseDetailsAndBorrowingBaseQuery,
  useGetUserQuery,
  FundingRequestWithFullDetailsFragmentGQLType,
  FundingRequestComponentTypeEnumGQLType,
} from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  ApproveFundingRequestButton,
  RejectFundingRequestButton,
} from './ApproveRejectButtons';
import { FundingImpact } from './FundingImpact';
import { colors } from 'packages/common-frontend/colors';
import { Breadcrumbs } from 'packages/common-frontend/components/Breadcrumbs';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  getUnexpectedErrorMessage,
  bankAccountToDisplayString,
  dollarNumberWithCommas,
} from 'packages/common-frontend/utils';
import { useGetCurrentCreditAgreementFullArtifactQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { FundingRequestDetailBorrowingBaseSummary } from 'packages/funding-requests/ui/FundingRequestDetailBorrowingBaseSummary';
import {
  StatusBadge,
  FundingRequestStatusBadge,
} from 'packages/funding-requests/ui/shared/StatusBadge';
import { useReportDownloadUrl } from 'packages/reports/hooks/useReportDownloadUrl';
import { VerificationApproveTables } from 'packages/verifications/ui/VerificationApproveTables';
import { FundingRequestInternalApproval } from '~/capitalProvider/domains/fundingRequests/ui/detail/FundingRequestInternalApproval';
import { SingleEntityDrawer as FundingRequestEventLogSingleEntityDrawer } from '~/core/routes/funding-requests/EntityEventLogDrawers';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { fundingRequestKeyToLabel } from '~/packages/funding-requests/constants';

const BorrowingBaseAvailabilityBadge = ({
  isBorrowingBaseAvailable,
}: {
  isBorrowingBaseAvailable: boolean;
}) => {
  return (
    <StatusBadge
      status={isBorrowingBaseAvailable ? 'success' : 'error'}
      icon={isBorrowingBaseAvailable ? 'circle-check' : 'circle-x'}
      label="Borrowing Base Available"
    />
  );
};

const InfoLabel = ({
  label,
  labelColor,
  bgColor,
}: {
  label: string;
  bgColor: StackProps['bgColor'];
  labelColor: TextProps['color'];
}) => (
  <HStack p="4px 8px" bgColor={bgColor}>
    <Text color={labelColor}>{label}</Text>
  </HStack>
);

const FundingRequestReport = ({
  report,
}: {
  report: {
    id: string;
    s3Path: string;
    reportType: ReportTypeGQLType;
    isDomainReport: boolean;
  };
}) => {
  const toast = useToast();

  const { id: reportId, reportType, s3Path, isDomainReport } = report;
  const reportName = s3Path.split('/').pop();
  finleyAssert(reportName, 's3 path expected to exist as a report name');
  const displayName = _.upperFirst(_.lowerCase(reportType));
  const extension = reportName.split('.').pop()?.toLocaleUpperCase() ?? '-';

  const { getReportDownloadUrl, error, loading } = useReportDownloadUrl({
    toast,
    reportId,
    reportName,
    isDomainReport,
  });
  if (error) {
    throw error;
  }
  return (
    <Tr>
      <Td>{displayName}</Td>
      <Td>{extension}</Td>
      <Td textAlign="center">
        <IconButton
          aria-label={`Download ${reportName}`}
          onClick={() => {
            Sentry.addBreadcrumb({
              category: 'ui.click',
              message: `Clicked download for ${reportName} in funding request`,
              level: Sentry.Severity.Info,
            });
            try {
              getReportDownloadUrl();
            } catch (err: any) {
              if (!toast.isActive(`${reportName}-getDownloadUrl`)) {
                errorToastConfig.title = `Error getting report download for ${reportName}`;
                toast(errorToastConfig);
              }
              throw err;
            }
          }}
          icon={<Icon icon="arrow-down-to-bracket" />}
          variant="ghost"
          isLoading={loading}
        />
      </Td>
    </Tr>
  );
};

const DownloadFundingRequestReports = ({
  fundingRequestReports,
}: {
  fundingRequestReports: {
    id: string;
    s3Path: string;
    reportType: ReportTypeGQLType;
    isDomainReport: boolean;
  }[];
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = React.useRef(null);
  return (
    <>
      <IconButton
        icon={<Icon icon="arrow-down-to-bracket" />}
        aria-label="download files"
        variant="solid"
        colorScheme="gray"
        onClick={onOpen}
        ref={btnRef}
      />
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={onClose}
        finalFocusRef={btnRef}
        size="lg"
      >
        <DrawerOverlay />
        <DrawerContent padding="24px">
          <HStack justify="space-between" alignItems="center">
            <DrawerHeader>Download</DrawerHeader>
            <IconButton
              aria-label="Close download drawer"
              icon={<Icon icon="x" />}
              onClick={onClose}
              variant="ghost"
            />
          </HStack>

          <DrawerBody>
            <TableContainer mt="32px">
              <Table>
                <Thead>
                  <Tr>
                    <Th>File name</Th>
                    <Th>File type</Th>
                    <Th textAlign="center">Download</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {fundingRequestReports.map(
                    (report: {
                      id: string;
                      s3Path: string;
                      reportType: ReportTypeGQLType;
                      isDomainReport: boolean;
                    }) => (
                      <FundingRequestReport key={report.id} report={report} />
                    ),
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export const TransactionDetailsRow = ({
  label,
  panel,
  amount,
  isInitiallyOpen = false,
}: {
  panel: React.ReactNode;
  label: React.ReactNode;
  amount: React.ReactNode;
  isInitiallyOpen?: boolean;
}) => {
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(isInitiallyOpen);

  return (
    <>
      <GridItem bgColor={colors.aurora[50]} p="16px" colSpan={2}>
        <Text fontWeight="bold">{label}</Text>
      </GridItem>
      <GridItem
        bgColor={colors.aurora[50]}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Text fontWeight="bold">{amount}</Text>
        <IconButton
          aria-label="expand transaction details"
          icon={
            <Icon
              icon={isPanelOpen ? 'circle-chevron-up' : 'circle-chevron-down'}
            />
          }
          variant="ghost"
          _focus={{ backgroundColor: 'none' }}
          _hover={{ backgroundColor: 'none' }}
          _active={{ backgroundColor: 'none' }}
          onClick={() => setIsPanelOpen(!isPanelOpen)}
        />
      </GridItem>
      {isPanelOpen && <GridItem gridColumn="1/-1">{panel}</GridItem>}
    </>
  );
};

const sortFundingRequestComponents = (
  fundingRequestComponents: FundingRequestWithFullDetailsFragmentGQLType['fundingRequestComponents'],
): FundingRequestWithFullDetailsFragmentGQLType['fundingRequestComponents'] => {
  const order: FundingRequestComponentTypeEnumGQLType[] = [
    'drawRequest',
    'collectionsRecycle',
    'collectionsPaydown',
    'curePaydown',
  ];

  return [...fundingRequestComponents].sort((a, b) => {
    const aIndex = order.indexOf(a.type);
    const bIndex = order.indexOf(b.type);
    return aIndex - bIndex;
  });
};

export const TransactionDetails = ({
  bankAccountsMap,
  fundingRequestComponents,
  fundingRequestMechanics,
}: {
  bankAccountsMap: Map<string, BankAccountWithInstitutionAndDesignationGQLType>;
  fundingRequestComponents: FundingRequestWithFullDetailsFragmentGQLType['fundingRequestComponents'];
  fundingRequestMechanics: CreditAgreement.DomainEntity.FundingRequestMechanics;
}) => {
  const sortedFundingRequestComponents = sortFundingRequestComponents(
    fundingRequestComponents,
  );

  return (
    <>
      <Heading as="h3" fontSize="2xl" m="40px 0px 24px">
        Transaction Details
      </Heading>
      <Grid
        templateColumns="repeat(3, 1fr)"
        templateRows="repeat(1, 56px)"
        rowGap="16px"
      >
        {sortedFundingRequestComponents.map(
          ({
            id,
            type,
            amount,
            sourceBankAccountId,
            destinationBankAccountId,
            targetFundingDate,
            postingDate,
          }) => {
            const destinationBankAccount = bankAccountsMap.get(
              destinationBankAccountId,
            );
            const sourceBankAccount = bankAccountsMap.get(sourceBankAccountId);

            // don't show if collections recycle or cure paydown not allowed
            if (
              type === 'collectionsRecycle' &&
              !fundingRequestMechanics.collectionsRecycle?.isAllowed
            ) {
              return null;
            }
            if (
              type === 'collectionsPaydown' &&
              !fundingRequestMechanics.collectionsPaydown?.isAllowed
            ) {
              return null;
            }

            return (
              <TransactionDetailsRow
                key={id}
                isInitiallyOpen={amount > 0}
                label={fundingRequestKeyToLabel[type]}
                amount={dollarNumberWithCommas(amount)}
                panel={
                  <Table variant="simple">
                    <Thead
                      background={colors.tableHeader}
                      borderBottom="1px solid lightgrey"
                      padding="8px 0"
                    >
                      <Tr>
                        <Th textAlign="left" width="20%">
                          Transaction
                        </Th>
                        <Th textAlign="center" width="10%">
                          Amount
                        </Th>
                        <Th textAlign="center" width="20%">
                          Source
                        </Th>
                        <Th textAlign="center" width="20%">
                          Destination
                        </Th>
                        <Th textAlign="center" width="15%">
                          Funding Date
                        </Th>
                        <Th textAlign="center" width="15%">
                          Settled Date
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      <Tr>
                        <Td textAlign="left" width="20%">
                          {fundingRequestKeyToLabel[type]}
                        </Td>
                        <Td textAlign="center" width="10%">
                          {dollarNumberWithCommas(amount)}
                        </Td>
                        <Td textAlign="center" width="20%">
                          {bankAccountToDisplayString(sourceBankAccount)}
                        </Td>
                        <Td textAlign="center" width="20%">
                          {bankAccountToDisplayString(destinationBankAccount)}
                        </Td>
                        <Td textAlign="center" width="15%">
                          {targetFundingDate
                            ? commonDate.fullDateStrToHumanDateString(
                                targetFundingDate,
                              )
                            : '-'}
                        </Td>
                        <Td textAlign="center" width="15%">
                          {postingDate
                            ? commonDate.fullDateStrToHumanDateString(
                                postingDate,
                              )
                            : '-'}
                        </Td>
                      </Tr>
                    </Tbody>
                  </Table>
                }
              />
            );
          },
        )}
      </Grid>
    </>
  );
};

export const FundingRequestDetailViewContainer = ({
  fundingRequestId,
  creditFacilityId,
  workflowInstanceId,
}: {
  fundingRequestId: string;
  creditFacilityId: string;
  workflowInstanceId?: string;
}) => {
  const toast = useToast();

  const { data: userData, loading: userLoading } = useGetUserQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });
  const {
    data: fundingRequestData,
    loading: loadingFundingRequestData,
    error,
  } = useGetFundingRequestWithBaseDetailsAndBorrowingBaseQuery({
    variables: { fundingRequestId },
  });

  const {
    data: creditAgreementData,
    loading: isCreditAgreementLoading,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    variables: { creditFacilityId },
    onError: () => {
      toast({
        ...errorToastConfig,
        id: 'errorGetCurrentCreditAgreementFullArtifact',
        title: 'Error loading credit agreement.',
      });
    },
  });

  if (
    !fundingRequestData ||
    loadingFundingRequestData ||
    !userData ||
    userLoading ||
    isCreditAgreementLoading ||
    !creditAgreementData ||
    !creditAgreementData.currentCreditAgreementFullArtifact
  ) {
    return <Skeleton data-testid="loading-funding-request" height="600px" />;
  }
  if (error) {
    return <Navigate to="/funding-requests" />;
  }

  const {
    fundingRequestWithFullDetails: {
      bankAccounts,
      created_at: createdAt,
      creditFacility,
      fundingRequestComponents,
      fundingRequestEvents,
      fundingRequestReports,
      status,
      reportingDate,
      verificationReport,
      // verificationReportEvents,
      borrowingBaseReportId, // @jendevelops going to grab initialBorrowingBaseReport as field resolver instead of having to grab it separately in downstream fetch
      borrowingBaseReport: proFormaBorrowingBaseReport,
      borrower: { name: borrowerName },
    },
  } = fundingRequestData;

  finleyAssert(
    fundingRequestEvents,
    'Funding request events expected to exist for funding request with full details',
  );

  finleyAssert(
    fundingRequestReports,
    'Funding request reports expected to exist for funding request with full details',
  );

  const bankAccountsMap = new Map<
    string,
    BankAccountWithInstitutionAndDesignationGQLType
  >(
    bankAccounts.map(
      (
        bankAccount: BankAccountWithInstitutionAndDesignationGQLType,
      ): [string, BankAccountWithInstitutionAndDesignationGQLType] => [
        bankAccount.id,
        bankAccount,
      ],
    ),
  );

  const {
    currentCreditAgreementFullArtifact: {
      creditAgreement,
      id: creditAgreementId,
    },
  } = creditAgreementData;

  const parsedCreditAgreement = parseCreditAgreementFullArtifact(
    creditAgreement,
  );

  finleyAssert(
    parsedCreditAgreement,
    `Expect parsed currentCreditAgreementFullArtifact to exist for [creditFacilityId ${creditFacilityId}] [creditAgreementId: ${creditAgreementId}]`,
  );

  const drawRequestComponent = fundingRequestComponents.find(
    (fundingRequestComponent) => fundingRequestComponent.type === 'drawRequest',
  );

  const collectionsRecycleComponent = fundingRequestComponents.find(
    (fundingRequestComponent) =>
      fundingRequestComponent.type === 'collectionsRecycle',
  );

  finleyAssert(
    drawRequestComponent,
    'Draw request component expected to exist for funding request',
  );

  finleyAssert(
    collectionsRecycleComponent,
    'Collections recycle component expected to exist for funding request',
  );

  return (
    <>
      <VStack spacing="24px" w="100%" alignItems="start">
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Funding Request', path: '/funding-requests' },
            {
              label: fundingRequestId,
              path: `/funding-requests/${fundingRequestId}`,
            },
          ]}
        />
        <HStack w="100%" spacing="24px">
          <Heading flexGrow={1}>Funding Request</Heading>
          <FundingRequestStatusBadge status={status} size="sm" />
          <BorrowingBaseAvailabilityBadge
            isBorrowingBaseAvailable={
              typeof proFormaBorrowingBaseReport.borrowingBaseAvailability ===
                'number' &&
              proFormaBorrowingBaseReport.borrowingBaseAvailability > 0
            }
          />
          <DownloadFundingRequestReports
            fundingRequestReports={fundingRequestReports}
          />
          <FundingRequestEventLogSingleEntityDrawer
            entityId={fundingRequestId}
          />
        </HStack>
        <HStack w="100%" spacing="16px">
          <InfoLabel
            label={creditFacility.name}
            bgColor="#E0F8FF"
            labelColor={colors.black[95]}
          />
          <InfoLabel
            label={borrowerName}
            bgColor={colors.black[10]}
            labelColor={colors.black[95]}
          />
          <InfoLabel
            label={`ID: ${fundingRequestId}`}
            bgColor={colors.black[10]}
            labelColor={colors.black[95]}
          />
          <InfoLabel
            label={`Request Date: ${commonDate.dateToHumanDateString(
              new Date(createdAt),
            )}`}
            bgColor={colors.black[10]}
            labelColor={colors.black[95]}
          />
        </HStack>
      </VStack>
      <Tabs mt="40px">
        <TabList>
          <Tab>Summary</Tab>
          <Tab>Borrowing Base</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <TransactionDetails
              fundingRequestComponents={fundingRequestComponents}
              bankAccountsMap={bankAccountsMap}
              fundingRequestMechanics={
                parsedCreditAgreement.fundingRequestMechanics
              }
            />
            <Heading as="h3" fontSize="2xl" mt="64px">
              Borrowing Base Overview
            </Heading>
            <FundingImpact
              borrowingBaseReportId={borrowingBaseReportId}
              displayPrincipalBalance={false}
            />
          </TabPanel>
          <TabPanel>
            {verificationReport ? (
              (status === 'rejected' || status === 'approved') && (
                <Box pt="32px">
                  <Heading as="h3" fontSize="2xl" pb="16px">
                    Borrowing Base Summary
                  </Heading>
                  <Box>
                    {/* @jendevelops [TODO-AssetVerification-postMVP]: add asset verification here, this is funding request detail page */}
                    <VerificationApproveTables
                      creditFacilityId={creditFacilityId}
                      creditAgreement={parsedCreditAgreement}
                      verificationReport={JSON.parse(
                        verificationReport.verificationReport,
                      )}
                      verificationReportId={
                        verificationReport.verificationReportId
                      }
                      verificationReportBorrowingBaseReportId={
                        verificationReport.borrowingBaseReportId
                      }
                      approvalReportNote={
                        verificationReport.approvalReportNote ?? undefined
                      }
                      selectedReportingDate={reportingDate}
                      viewMode="viewOnly"
                    />
                  </Box>
                </Box>
              )
            ) : (
              <FundingRequestDetailBorrowingBaseSummary
                borrowingBaseReportId={borrowingBaseReportId}
                creditFacilityId={creditFacilityId}
              />
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* If there is no verification report and there IS a workflow instance for a funding request has been approved or rejected. Show READ ONLY approval UI. */}
      {workflowInstanceId &&
        !verificationReport &&
        (status === 'rejected' || status === 'approved') && (
          <FundingRequestInternalApproval
            fundingRequestId={fundingRequestId}
            workflowInstanceId={workflowInstanceId}
            creditFacilityId={
              fundingRequestData.fundingRequestWithFullDetails.creditFacilityId
            }
            creditFacilityName={
              fundingRequestData.fundingRequestWithFullDetails.creditFacility
                .name
            }
            readOnly
          />
        )}

      {/* If there is no verification report and there IS a workflow instance for a funding request has beens ubmitted. Show EDIT approval UI. */}
      {status === 'submitted' &&
        !verificationReport &&
        (workflowInstanceId ? (
          <FundingRequestInternalApproval
            fundingRequestId={fundingRequestId}
            workflowInstanceId={workflowInstanceId}
            creditFacilityId={
              fundingRequestData.fundingRequestWithFullDetails.creditFacilityId
            }
            creditFacilityName={
              fundingRequestData.fundingRequestWithFullDetails.creditFacility
                .name
            }
          />
        ) : (
          <Flex
            mt="25px"
            box-shadow="0px -2px 4px -1px rgba(0, 0, 0, 0.06)"
            w="100%"
            alignItems="center"
            justifyContent="flex-end"
            gap="16px"
          >
            <RejectFundingRequestButton
              fundingRequest={fundingRequestData.fundingRequestWithFullDetails}
            />
            <ApproveFundingRequestButton
              fundingRequest={fundingRequestData.fundingRequestWithFullDetails}
            />
          </Flex>
        ))}
    </>
  );
};
